import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FilterChip, SearchField, StatePanel } from '@3dspinit/ui';
import { api, type FeedItem } from '../lib/api';
import { ObjectCard } from '../components/ObjectCard';

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [input, setInput] = useState(q);
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freeOnly, setFreeOnly] = useState(params.get('offer') === 'free');
  const requestId = useRef(0);

  useEffect(() => {
    setInput(q);
  }, [q]);

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setItems([]);
      setLoading(false);
      setError(null);
      return;
    }

    const id = ++requestId.current;
    setLoading(true);
    setError(null);

    const timer = window.setTimeout(() => {
      void api
        .search(term)
        .then((r) => {
          if (id !== requestId.current) return;
          let results = r.items;
          if (freeOnly) {
            results = results.filter((item) => !item.priceCents || item.priceCents <= 0);
          }
          setItems(results);
        })
        .catch((e) => {
          if (id !== requestId.current) return;
          setError(e instanceof Error ? e.message : 'Search failed');
          setItems([]);
        })
        .finally(() => {
          if (id === requestId.current) setLoading(false);
        });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [q, freeOnly]);

  function submit(next: string) {
    const paramsNext: Record<string, string> = {};
    if (next.trim()) paramsNext.q = next.trim();
    if (freeOnly) paramsNext.offer = 'free';
    setParams(paramsNext);
  }

  return (
    <main className="page">
      <h1 style={{ fontFamily: 'var(--font-display)' }}>Search objects</h1>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          submit(input);
        }}
      >
        <SearchField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search guitars, clothing, museum objects…"
          onClear={() => {
            setInput('');
            setParams({});
          }}
        />
      </form>

      <div className="category-row" style={{ marginTop: '1rem' }} role="group" aria-label="Filters">
        <FilterChip
          selected={freeOnly}
          onClick={() => {
            setFreeOnly((v) => !v);
            const next = new URLSearchParams(params);
            if (!freeOnly) next.set('offer', 'free');
            else next.delete('offer');
            setParams(next);
          }}
        >
          Free
        </FilterChip>
        {freeOnly ? (
          <button type="button" className="btn ghost" onClick={() => { setFreeOnly(false); const next = new URLSearchParams(params); next.delete('offer'); setParams(next); }}>
            Clear all
          </button>
        ) : null}
      </div>

      <p className="muted" role="status" aria-live="polite" style={{ marginTop: '0.75rem' }}>
        {loading ? 'Searching…' : q.trim() ? `${items.length} result${items.length === 1 ? '' : 's'}` : 'Enter a search term'}
      </p>

      {error && <StatePanel variant="error" title="Search unavailable" message={error} />}

      {!loading && !error && q.trim() && items.length === 0 && (
        <StatePanel
          variant="empty"
          title="No objects match these filters"
          message="Try a broader term or explore categories on the homepage."
          actionLabel="Explore catalog"
          onAction={() => { window.location.href = '/'; }}
        />
      )}

      <div className="masonry" style={{ marginTop: '1rem' }}>
        {items.map((item) => (
          <ObjectCard key={item.id} item={item} />
        ))}
      </div>

      <p style={{ marginTop: '1rem' }}>
        <Link to="/">Back to Explore</Link>
      </p>
    </main>
  );
}
